// ── Unified Monetization Mock ──
// Single source of truth. No double counting.
// Revenue share: 70% Radio / 30% RFW (ONE consistent percentage)

// ═══════════════════════════════════════════
// Revenue Share
// ═══════════════════════════════════════════
export const REVENUE_SHARE_RADIO = 70;
export const REVENUE_SHARE_RFW = 30;

// ═══════════════════════════════════════════
// Revenue Totals (no double counting)
// ═══════════════════════════════════════════
export const ownAdsRevenue = 4850;
export const rfwGrossRevenue = 12300;
export const rfwRadioRevenue = 8610; // 70% of 12300
export const rfwPlatformRevenue = 3690; // 30% of 12300
export const sponsorshipsRevenue = 8200;
export const marketplaceRevenue = 3100;

export const totalRadioRevenue = ownAdsRevenue + rfwRadioRevenue + sponsorshipsRevenue + marketplaceRevenue; // 24760
export const totalGrossRevenue = ownAdsRevenue + rfwGrossRevenue + sponsorshipsRevenue + marketplaceRevenue; // 28450

// ═══════════════════════════════════════════
// Fees & Payouts
// ═══════════════════════════════════════════
export const platformFees = 480;
export const paymentProcessingFees = 210;
export const totalFees = platformFees + paymentProcessingFees; // 690
export const pendingPayouts = 3120;
export const completedPayouts = totalRadioRevenue - pendingPayouts - totalFees; // 20950

// ═══════════════════════════════════════════
// Global KPIs
// ═══════════════════════════════════════════
export const totalImpressions = 229700;
export const totalClicks = 9730;
export const avgCtr = 4.2;
export const monthLabel = 'Junio 2026';

// ═══════════════════════════════════════════
// Revenue Source Cards (4 sources, no "Campañas" double count)
// ═══════════════════════════════════════════
export interface UnifiedRevenueSource {
  id: string;
  name: string;
  icon: string;
  description: string;
  ingresos: number;
  impresiones: number;
  clicks: number;
  ctr: number;
  count: number;
  countLabel: string;
  trend: number;
  color: string;
  bgLight: string;
  bgBar: string;
}

export const revenueSources: UnifiedRevenueSource[] = [
  {
    id: 'propios',
    name: 'Ingresos Propios',
    icon: 'ri-store-2-line',
    description: 'Publicidad vendida directamente por tu radio. 100% del revenue es tuyo.',
    ingresos: ownAdsRevenue,
    impresiones: 42500,
    clicks: 1870,
    ctr: 4.4,
    count: 4,
    countLabel: 'anunciantes directos',
    trend: 12.5,
    color: 'text-emerald-600',
    bgLight: 'bg-emerald-50',
    bgBar: 'bg-emerald-500',
  },
  {
    id: 'rfw',
    name: 'Ingresos Red RFW',
    icon: 'ri-global-line',
    description: 'Campañas de la red publicitaria RFW. Reparto 70/30 automático.',
    ingresos: rfwRadioRevenue,
    impresiones: 187200,
    clicks: 7860,
    ctr: 4.2,
    count: 8,
    countLabel: 'campañas activas',
    trend: 18.3,
    color: 'text-violet-600',
    bgLight: 'bg-violet-50',
    bgBar: 'bg-violet-500',
  },
  {
    id: 'patrocinadores',
    name: 'Patrocinadores',
    icon: 'ri-star-line',
    description: 'Ingresos por patrocinios de marcas y empresas asociadas.',
    ingresos: sponsorshipsRevenue,
    impresiones: 56000,
    clicks: 2120,
    ctr: 3.8,
    count: 6,
    countLabel: 'patrocinadores activos',
    trend: 8.2,
    color: 'text-rose-600',
    bgLight: 'bg-rose-50',
    bgBar: 'bg-rose-500',
  },
  {
    id: 'marketplace',
    name: 'Marketplace',
    icon: 'ri-store-line',
    description: 'Espacios publicitarios publicados en el marketplace de RFW.',
    ingresos: marketplaceRevenue,
    impresiones: 18400,
    clicks: 740,
    ctr: 4.0,
    count: 3,
    countLabel: 'listings activos',
    trend: 22.4,
    color: 'text-cyan-600',
    bgLight: 'bg-cyan-50',
    bgBar: 'bg-cyan-500',
  },
];

// ═══════════════════════════════════════════
// Monthly Revenue Chart
// ═══════════════════════════════════════════
export interface UnifiedMonthlyPoint {
  month: string;
  propio: number;
  rfw: number;
  patrocinadores: number;
  marketplace: number;
}

export const monthlyRevenue: UnifiedMonthlyPoint[] = [
  { month: 'Ene', propio: 4200, rfw: 6860, patrocinadores: 7500, marketplace: 2100 },
  { month: 'Feb', propio: 4500, rfw: 7140, patrocinadores: 7800, marketplace: 2400 },
  { month: 'Mar', propio: 4100, rfw: 8050, patrocinadores: 7200, marketplace: 1900 },
  { month: 'Abr', propio: 4800, rfw: 8260, patrocinadores: 8100, marketplace: 2800 },
  { month: 'May', propio: 4600, rfw: 8750, patrocinadores: 8000, marketplace: 3200 },
  { month: 'Jun', propio: 4850, rfw: 8610, patrocinadores: 8200, marketplace: 3100 },
];

// ═══════════════════════════════════════════
// Own Ads Details
// ═══════════════════════════════════════════
export interface UnifiedOwnAd {
  id: number;
  client: string;
  price: number;
  startDate: string;
  endDate: string;
  status: string;
  impressions: number;
  clicks: number;
  ctr: number;
  revenue: number;
}

export const ownAds: UnifiedOwnAd[] = [
  { id: 1, client: 'Cerveza Andes', price: 1500, startDate: '2026-04-01', endDate: '2026-07-01', status: 'activo', impressions: 12500, clicks: 580, ctr: 4.6, revenue: 1500 },
  { id: 2, client: 'AutoRepuestos Mendoza', price: 1200, startDate: '2026-05-01', endDate: '2026-08-01', status: 'activo', impressions: 10500, clicks: 430, ctr: 4.1, revenue: 1200 },
  { id: 3, client: 'Restaurante La Parrilla', price: 950, startDate: '2026-03-15', endDate: '2026-06-15', status: 'activo', impressions: 8200, clicks: 340, ctr: 4.1, revenue: 950 },
  { id: 4, client: 'Seguros La Protección', price: 1200, startDate: '2026-05-15', endDate: '2026-08-15', status: 'activo', impressions: 11300, clicks: 520, ctr: 4.6, revenue: 1200 },
];

// ═══════════════════════════════════════════
// RFW Campaigns Details
// ═══════════════════════════════════════════
export interface UnifiedRfwCampaign {
  id: number;
  campaignName: string;
  advertiserName: string;
  totalAmount: number;
  radioPercent: number;
  radioEarnings: number;
  impressions: number;
  clicks: number;
  ctr: number;
  startDate: string;
  endDate: string;
  status: string;
}

export const rfwCampaigns: UnifiedRfwCampaign[] = [
  { id: 1, campaignName: 'Lanzamiento App Móvil RFW', advertiserName: 'RadioFrecuenciaWeb', totalAmount: 3500, radioPercent: 70, radioEarnings: 2450, impressions: 48500, clicks: 2050, ctr: 4.2, startDate: '2026-04-01', endDate: '2026-07-01', status: 'activo' },
  { id: 2, campaignName: 'Copa América Especial', advertiserName: 'AutoRepuestos Mendoza', totalAmount: 2800, radioPercent: 70, radioEarnings: 1960, impressions: 39500, clicks: 1620, ctr: 4.1, startDate: '2026-06-01', endDate: '2026-07-31', status: 'activo' },
  { id: 3, campaignName: 'Noticiero Matutino Sponsor', advertiserName: 'Banco Santander', totalAmount: 2200, radioPercent: 70, radioEarnings: 1540, impressions: 31200, clicks: 1150, ctr: 3.7, startDate: '2026-06-01', endDate: '2026-09-01', status: 'activo' },
  { id: 4, campaignName: 'Fitness Total Promo', advertiserName: 'Gimnasio PowerFit', totalAmount: 1600, radioPercent: 70, radioEarnings: 1120, impressions: 22800, clicks: 890, ctr: 3.9, startDate: '2026-05-01', endDate: '2026-08-01', status: 'activo' },
  { id: 5, campaignName: 'Gastronomía Regional Tour', advertiserName: 'Restaurante La Parrilla', totalAmount: 1400, radioPercent: 70, radioEarnings: 980, impressions: 17500, clicks: 680, ctr: 3.9, startDate: '2026-04-15', endDate: '2026-07-15', status: 'activo' },
  { id: 6, campaignName: 'Indumentaria Deportiva Ofertas', advertiserName: 'Deportes Total', totalAmount: 1900, radioPercent: 70, radioEarnings: 1330, impressions: 26200, clicks: 1080, ctr: 4.1, startDate: '2026-06-01', endDate: '2026-08-01', status: 'activo' },
  { id: 7, campaignName: 'Tecnología Hogar Smart', advertiserName: 'ElectroHogar SA', totalAmount: 2100, radioPercent: 70, radioEarnings: 1470, impressions: 29800, clicks: 1250, ctr: 4.2, startDate: '2026-05-15', endDate: '2026-08-15', status: 'activo' },
  { id: 8, campaignName: 'Turismo Regional Verano', advertiserName: 'Viajes Mendoza', totalAmount: 1700, radioPercent: 70, radioEarnings: 1190, impressions: 21500, clicks: 860, ctr: 4.0, startDate: '2026-06-01', endDate: '2026-09-01', status: 'activo' },
];

// ═══════════════════════════════════════════
// Sponsors
// ═══════════════════════════════════════════
export interface UnifiedSponsor {
  id: number;
  name: string;
  type: string;
  amount: number;
  since: string;
  logo: string;
}

export const sponsors: UnifiedSponsor[] = [
  { id: 1, name: 'Cerveza Andes', type: 'propio', amount: 1500, since: '2026-04-01', logo: 'CA' },
  { id: 2, name: 'AutoRepuestos Mdza.', type: 'propio', amount: 1200, since: '2026-05-01', logo: 'AR' },
  { id: 3, name: 'Banco Santander', type: 'rfw', amount: 1540, since: '2026-06-01', logo: 'BS' },
  { id: 4, name: 'Gimnasio PowerFit', type: 'rfw', amount: 1120, since: '2026-05-01', logo: 'PF' },
  { id: 5, name: 'Deportes Total', type: 'rfw', amount: 1330, since: '2026-06-01', logo: 'DT' },
  { id: 6, name: 'Viajes Mendoza', type: 'rfw', amount: 1190, since: '2026-06-01', logo: 'VM' },
];

// ═══════════════════════════════════════════
// Marketplace Listings
// ═══════════════════════════════════════════
export interface UnifiedMarketplaceItem {
  id: number;
  title: string;
  description: string;
  price: number;
  status: string;
  impressions: number;
  clicks: number;
  ctr: number;
  postedSince: string;
  category: string;
}

export const marketplaceItems: UnifiedMarketplaceItem[] = [
  { id: 1, title: 'Banner Home Principal', description: 'Espacio premium en la home de la radio. Máxima visibilidad.', price: 1500, status: 'activo', impressions: 8200, clicks: 340, ctr: 4.1, postedSince: '2026-05-01', category: 'Display' },
  { id: 2, title: 'Menciones Programa Matutino', description: '3 menciones por programa en el horario de 8 a 12. Lunes a viernes.', price: 900, status: 'activo', impressions: 5800, clicks: 220, ctr: 3.8, postedSince: '2026-05-15', category: 'Audio' },
  { id: 3, title: 'Newsletter Semanal Footer', description: 'Publicidad en el footer del newsletter semanal. 4 envíos al mes.', price: 700, status: 'pendiente', impressions: 4400, clicks: 180, ctr: 4.1, postedSince: '2026-06-10', category: 'Email' },
];

// ═══════════════════════════════════════════
// Revenue Analytics Data
// ═══════════════════════════════════════════
export type MonetizationTimePeriod = 'hoy' | 'semana' | 'mes' | 'ano';

export interface UnifiedPeriodData {
  value: number;
  trend: 'up' | 'down' | 'flat';
  trendLabel: string;
}

export interface UnifiedMonetizationKpi {
  id: string;
  label: string;
  icon: string;
  color: string;
  unit: string;
  hoy: UnifiedPeriodData;
  semana: UnifiedPeriodData;
  mes: UnifiedPeriodData;
  ano: UnifiedPeriodData;
}

export const monetizationTimePeriodLabels: Record<MonetizationTimePeriod, string> = {
  hoy: 'Hoy',
  semana: 'Semana',
  mes: 'Mes',
  ano: 'Año',
};

export const revenueKpis: UnifiedMonetizationKpi[] = [
  {
    id: 'ingresos-propios',
    label: 'Ingresos Propios',
    icon: 'ri-hand-coin-line',
    color: 'emerald',
    unit: '$',
    hoy: { value: 162, trend: 'up', trendLabel: '+8.4%' },
    semana: { value: 1130, trend: 'up', trendLabel: '+12.1%' },
    mes: { value: ownAdsRevenue, trend: 'up', trendLabel: '+9.7%' },
    ano: { value: 58200, trend: 'up', trendLabel: '+18.3%' },
  },
  {
    id: 'ingresos-rfw',
    label: 'Ingresos RFW',
    icon: 'ri-exchange-dollar-line',
    color: 'violet',
    unit: '$',
    hoy: { value: 287, trend: 'up', trendLabel: '+5.2%' },
    semana: { value: 2010, trend: 'up', trendLabel: '+7.8%' },
    mes: { value: rfwRadioRevenue, trend: 'up', trendLabel: '+6.3%' },
    ano: { value: 103320, trend: 'up', trendLabel: '+14.6%' },
  },
  {
    id: 'patrocinadores',
    label: 'Patrocinadores',
    icon: 'ri-shake-hands-line',
    color: 'rose',
    unit: '$',
    hoy: { value: 273, trend: 'flat', trendLabel: '+0.0%' },
    semana: { value: 1910, trend: 'up', trendLabel: '+3.2%' },
    mes: { value: sponsorshipsRevenue, trend: 'up', trendLabel: '+5.1%' },
    ano: { value: 98400, trend: 'up', trendLabel: '+11.9%' },
  },
  {
    id: 'marketplace',
    label: 'Marketplace',
    icon: 'ri-store-2-line',
    color: 'indigo',
    unit: '$',
    hoy: { value: 103, trend: 'up', trendLabel: '+4.8%' },
    semana: { value: 720, trend: 'up', trendLabel: '+6.1%' },
    mes: { value: marketplaceRevenue, trend: 'up', trendLabel: '+8.9%' },
    ano: { value: 37200, trend: 'up', trendLabel: '+13.2%' },
  },
];

export const performanceKpis: UnifiedMonetizationKpi[] = [
  {
    id: 'ctr',
    label: 'CTR',
    icon: 'ri-mouse-line',
    color: 'sky',
    unit: '%',
    hoy: { value: 3.8, trend: 'up', trendLabel: '+0.4pp' },
    semana: { value: 4.1, trend: 'up', trendLabel: '+0.3pp' },
    mes: { value: 3.9, trend: 'up', trendLabel: '+0.2pp' },
    ano: { value: 3.6, trend: 'up', trendLabel: '+0.5pp' },
  },
  {
    id: 'impresiones',
    label: 'Impresiones',
    icon: 'ri-eye-line',
    color: 'orange',
    unit: '',
    hoy: { value: 1240, trend: 'up', trendLabel: '+8.2%' },
    semana: { value: 8510, trend: 'up', trendLabel: '+11.4%' },
    mes: { value: 35600, trend: 'up', trendLabel: '+9.8%' },
    ano: { value: 412800, trend: 'up', trendLabel: '+16.5%' },
  },
  {
    id: 'clicks',
    label: 'Clicks',
    icon: 'ri-cursor-line',
    color: 'emerald',
    unit: '',
    hoy: { value: 47, trend: 'up', trendLabel: '+10.1%' },
    semana: { value: 349, trend: 'up', trendLabel: '+13.6%' },
    mes: { value: 1388, trend: 'up', trendLabel: '+12.3%' },
    ano: { value: 14860, trend: 'up', trendLabel: '+19.2%' },
  },
  {
    id: 'conversiones',
    label: 'Conversiones',
    icon: 'ri-check-double-line',
    color: 'rose',
    unit: '',
    hoy: { value: 12, trend: 'up', trendLabel: '+6.7%' },
    semana: { value: 84, trend: 'up', trendLabel: '+9.3%' },
    mes: { value: 342, trend: 'up', trendLabel: '+8.1%' },
    ano: { value: 3890, trend: 'up', trendLabel: '+15.8%' },
  },
];

// ═══════════════════════════════════════════
// Tops Data
// ═══════════════════════════════════════════
export interface UnifiedTopItem {
  rank: number;
  name: string;
  subtitle: string;
  value: number;
  unit: string;
  trend: 'up' | 'down' | 'flat';
  extraLabel?: string;
  extraValue?: string;
}

export interface UnifiedTopsData {
  campanas: UnifiedTopItem[];
  patrocinadores: UnifiedTopItem[];
  marketplace: UnifiedTopItem[];
}

export const topsData: UnifiedTopsData = {
  campanas: [
    { rank: 1, name: 'Verano RFW 2026', subtitle: 'Campaña nacional · 45 radios', value: 3850, unit: '$', trend: 'up', extraLabel: 'CTR', extraValue: '4.8%' },
    { rank: 2, name: 'Lanzamiento App', subtitle: 'Digital + Radio · 32 radios', value: 2840, unit: '$', trend: 'up', extraLabel: 'CTR', extraValue: '4.2%' },
    { rank: 3, name: 'Patrocinio Fiesta Nacional', subtitle: 'Evento en vivo · 18 radios', value: 2160, unit: '$', trend: 'up', extraLabel: 'CTR', extraValue: '3.9%' },
    { rank: 4, name: 'Back to School', subtitle: 'Segmento juvenil · 28 radios', value: 1750, unit: '$', trend: 'flat', extraLabel: 'CTR', extraValue: '3.5%' },
    { rank: 5, name: 'Promo Otoño Musical', subtitle: 'Campaña temática · 22 radios', value: 1520, unit: '$', trend: 'up', extraLabel: 'CTR', extraValue: '3.7%' },
    { rank: 6, name: 'Día del Padre Especial', subtitle: 'Campaña estacional · 30 radios', value: 1280, unit: '$', trend: 'down', extraLabel: 'CTR', extraValue: '3.1%' },
    { rank: 7, name: 'Navidad Solidaria', subtitle: 'Campaña social · 50 radios', value: 450, unit: '$', trend: 'up', extraLabel: 'CTR', extraValue: '5.2%' },
  ],
  patrocinadores: [
    { rank: 1, name: 'Cerveza Patagonia', subtitle: 'Patrocinador premium · 12 meses', value: 4200, unit: '$', trend: 'up', extraLabel: 'Espacios', extraValue: '8 activos' },
    { rank: 2, name: 'Banco Macro', subtitle: 'Patrocinador financiero · 6 meses', value: 3150, unit: '$', trend: 'up', extraLabel: 'Espacios', extraValue: '5 activos' },
    { rank: 3, name: 'Movistar Argentina', subtitle: 'Telecomunicaciones · 9 meses', value: 2800, unit: '$', trend: 'flat', extraLabel: 'Espacios', extraValue: '6 activos' },
    { rank: 4, name: 'YPF', subtitle: 'Energía · 6 meses', value: 1950, unit: '$', trend: 'up', extraLabel: 'Espacios', extraValue: '4 activos' },
    { rank: 5, name: 'Mercado Libre', subtitle: 'E-commerce · 3 meses', value: 1680, unit: '$', trend: 'up', extraLabel: 'Espacios', extraValue: '3 activos' },
  ],
  marketplace: [
    { rank: 1, name: 'Agencia Creativa Sur', subtitle: 'Anunciante nuevo · Presupuesto alto', value: 920, unit: '$', trend: 'up', extraLabel: 'Propuestas', extraValue: '3 enviadas' },
    { rank: 2, name: 'Restaurante El Fuego', subtitle: 'Local · Presupuesto medio', value: 680, unit: '$', trend: 'up', extraLabel: 'Propuestas', extraValue: '2 enviadas' },
    { rank: 3, name: 'Inmobiliaria del Valle', subtitle: 'Regional · Presupuesto medio', value: 540, unit: '$', trend: 'flat', extraLabel: 'Propuestas', extraValue: '1 enviada' },
    { rank: 4, name: 'Tienda Online ModaYa', subtitle: 'Digital · Presupuesto bajo', value: 380, unit: '$', trend: 'up', extraLabel: 'Propuestas', extraValue: '2 enviadas' },
    { rank: 5, name: 'Gimnasio PowerFit', subtitle: 'Local · Presupuesto bajo', value: 250, unit: '$', trend: 'down', extraLabel: 'Propuestas', extraValue: '1 enviada' },
  ],
};

// ═══════════════════════════════════════════
// Config Modules
// ═══════════════════════════════════════════
export interface UnifiedConfigModule {
  id: string;
  label: string;
  icon: string;
  color: string;
  desc: string;
  participating: boolean;
  revenueNote?: string;
}

export const configModules: UnifiedConfigModule[] = [
  {
    id: 'propia',
    label: 'Publicidad Propia',
    icon: 'ri-advertisement-line',
    color: 'bg-emerald-500',
    desc: 'Gestioná y vendé tus propios espacios publicitarios directamente a anunciantes. Vos definís precios, ubicaciones y duraciones. 100% de la ganancia para tu radio.',
    participating: true,
    revenueNote: '100% de la ganancia',
  },
  {
    id: 'rfw',
    label: 'Red Publicitaria RFW',
    icon: 'ri-global-line',
    color: 'bg-amber-500',
    desc: 'Conectate a la red publicitaria de RadioFrecuenciaWeb. RFW gestiona los anunciantes y llena tus espacios vacíos automáticamente. Revenue share 70/30.',
    participating: true,
    revenueNote: `Revenue Share ${REVENUE_SHARE_RADIO}/${REVENUE_SHARE_RFW}`,
  },
  {
    id: 'marketplace',
    label: 'Marketplace',
    icon: 'ri-store-2-line',
    color: 'bg-violet-500',
    desc: 'Publicá productos, servicios o contenido exclusivo en el marketplace de RFW. Los oyentes compran directamente desde tu perfil.',
    participating: false,
    revenueNote: '100% tuyo',
  },
  {
    id: 'patrocinios',
    label: 'Patrocinios',
    icon: 'ri-hand-heart-line',
    color: 'bg-rose-500',
    desc: 'Activá el módulo de patrocinios para que marcas y empresas te encuentren. Recibí propuestas de patrocinio para programas, eventos y contenido exclusivo.',
    participating: false,
    revenueNote: 'Propuestas directas',
  },
];

// ═══════════════════════════════════════════
// Unified Data Object
// ═══════════════════════════════════════════
export const unifiedMonetizationData = {
  monthLabel,
  totalRadioRevenue,
  totalGrossRevenue,
  totalImpressions,
  totalClicks,
  avgCtr,
  revenueShare: { radio: REVENUE_SHARE_RADIO, rfw: REVENUE_SHARE_RFW },
  fees: { platform: platformFees, processing: paymentProcessingFees, total: totalFees },
  payouts: { pending: pendingPayouts, completed: completedPayouts },
  sources: revenueSources,
  monthlyRevenue,
  ownAds,
  rfwCampaigns,
  sponsors,
  marketplaceItems,
  revenueKpis,
  performanceKpis,
  tops: topsData,
  configModules,
};